import React from 'react';
import { connect } from 'react-redux';
import _ from 'underscore';

import { Messages } from '~src/codegen/Localize';
import { angularProviderHOC } from '~common/react/AngularProviderHOC';
import { ReactDirectiveProps } from '~src/ts/directives/reactDirectiveFactory';
import { AngularContext } from '~src/ts/factories/AngularContextFactory';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { BrowserTitleService } from '~commonInfra/services/BrowserTitleService';
import { getSessionManagementServiceInstance, SessionManagementService } from '~src/sessionManagement/SessionManagementService';
import { SessionServerSelectors, SessionUserSelectors } from '~selectors/SessionInfoSelectors';
import { AppState } from '../../state/AppState';
import { SiteSettingsSubspaceName } from '../../state/SpaceInfo';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { Subspace, TabbedSpace } from '../TabbedSpace';
import { AuthenticationSiteSettingsSubspace } from './AuthenticationSettingsSubspace';
import { BridgeSiteSettingsSubspace } from './BridgeSiteSettingsSubspace';
import { ExtensionsSettingsSubspace } from './ExtensionsSettingsSubspace';
import { GeneralSiteSettingsSubspace } from './GeneralSiteSettingsSubspace';
import { ServerSettingsUnauthenticated } from '~src/settings/services/ServerSettingsUnauthenticated';
import { HomeUserUtils } from '~src/home/utils/HomeUserUtils';
import { SiteRoles } from '~src/ts/ServerApi/types/ISiteRole';
import {
  IntegrationsSiteSettingsSubspaceComponent,
  IntegrationsSubspaceName
} from '~src/app/spaces/IntegrationsSettings/IntegrationsSiteSettingsSubspace';
export interface SiteSettingsSpaceOwnProps extends ReactDirectiveProps {
  readonly urls: SpacesSiteUrls;
  readonly siteName: string;
  readonly subspaceName: SiteSettingsSubspaceName;
}

export interface SiteSettingsAngularProps {
  readonly server: SessionManagementService;
}

export interface SiteSettingsStateProps {
  readonly isSessionMultiSite: boolean;
  readonly isSessionServerAdmin: boolean;
  readonly userRole: string;
  readonly isOnlineEnvironment: boolean;
}

export type SiteSettingsNonAngularProps = SiteSettingsSpaceOwnProps & SiteSettingsStateProps;
export type SiteSettingsSpaceProps = SiteSettingsNonAngularProps & SiteSettingsAngularProps;

export class SiteSettingsSpaceComponent extends React.PureComponent<SiteSettingsSpaceProps> {

  public componentDidMount() {
    BrowserTitleService.setTitleWithTranslatedSpace({
      translatedSpaceName: this.props.isSessionMultiSite ? Messages.siteSettings() : Messages.settings()
    });
  }

  public render() {
    const subspaces = getSettingsSubspaces(
      this.props.urls,
      this.props.siteName,
      this.props.angularContext,
      this.props.server,
      this.props.isSessionMultiSite,
      this.props.isSessionServerAdmin,
      this.props.userRole,
      this.props.isOnlineEnvironment
    );

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.subspaceName}
        urlForSubspace={this.getSettingsUrl}
        mastheadElement={null}
        testIdPrefix={'site-settings'}
        spaceItemName={''}
      />
    );
  }

  private static settingsUrlBySubspace: Record<SiteSettingsSubspaceName, (urls: SpacesSiteUrls) => string> = {
    settings: (urls) => urls.settingsUrl(),
    extensions: (urls) =>  urls.settingsExtensionsUrl(),
    bridge: (urls) =>  urls.settingsBridgeUrl(),
    authentication: (urls) =>  urls.settingsAuthenticationUrl(),
    integrations: (urls) => urls.settingsIntegrationsUrl(),
  };

  private getSettingsUrl = (subspaceName?: SiteSettingsSubspaceName) => {
    if (subspaceName) {
      return SiteSettingsSpaceComponent.settingsUrlBySubspace[subspaceName](this.props.urls);
    }
    return '';
  }
}

export function getSettingsSubspaces(
  urls: SpacesSiteUrls,
  siteName: string,
  angularContext: AngularContext,
  server: SessionManagementService,
  isSessionMultiSite: boolean,
  isSessionServerAdmin: boolean,
  userRole: string,
  isOnlineEnvironment: boolean
): ReadonlyArray<Subspace> {
  return _.compact<_.List<Subspace | false>>([
    {
      name: 'settings',
      tabLabel: Messages.general(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <GeneralSiteSettingsSubspace
          siteName={siteName}
          urls={urls}
          angularContext={angularContext}
          server={server}
          isSessionMultiSite={isSessionMultiSite}
          isSessionServerAdmin={isSessionServerAdmin}
        />
      )
    },
    ServerSettingsUnauthenticated.isSiteSamlEnabled() && {
      name: 'authentication',
      tabLabel: Messages.authentication(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <AuthenticationSiteSettingsSubspace
          siteName={siteName}
          urls={urls}
          angularContext={angularContext}
          server={server}
          isSessionMultiSite={isSessionMultiSite}
          isSessionServerAdmin={isSessionServerAdmin}
        />
      )
    },
    ServerSettingsUnauthenticated.isBridgeEnabled() && {
      name: 'bridge',
      tabLabel: Messages.bridge(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <BridgeSiteSettingsSubspace
          siteName={siteName}
          urls={urls}
          angularContext={angularContext}
          server={server}
          isSessionMultiSite={isSessionMultiSite}
          isSessionServerAdmin={isSessionServerAdmin}
        />
      )
    },
    FeatureFlags.extensionsSettingsTabEnabled && {
      name: 'extensions',
      tabLabel: Messages.extensionsSettings_title(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <ExtensionsSettingsSubspace
          isServerPage={false}
          siteName={siteName}
          urls={urls}
          angularContext={angularContext}
          server={server}
          isSessionMultiSite={isSessionMultiSite}
          isSessionServerAdmin={isSessionServerAdmin}
        />
      )
    },
    FeatureFlags.publishingSalesforceBundlesEnabled &&
    (userRole === SiteRoles.ServerAdministrator || userRole === SiteRoles.SiteAdministratorCreator) && {
      name: IntegrationsSubspaceName,
      tabLabel: Messages.connectToSalesforce_settingsTabTitle(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <IntegrationsSiteSettingsSubspaceComponent
          siteName={siteName}
          urls={urls}
          server={server}
          isSessionMultiSite={isSessionMultiSite}
          isSessionServerAdmin={isSessionServerAdmin}
          isOnlineEnvironment={isOnlineEnvironment}
        />
      )
    },
  ]);
}

export const ConnectedSiteSettingsSpace: React.ComponentClass<SiteSettingsSpaceOwnProps & SiteSettingsAngularProps>
  = connect((state: AppState) => ({
    isSessionMultiSite: SessionUserSelectors.isSessionMultiSite(state),
    isSessionServerAdmin: SessionUserSelectors.isSessionServerAdmin(state),
    userRole: HomeUserUtils.getRole(state),
    isOnlineEnvironment: SessionServerSelectors.isOnlineEnvironment(state),
  }))(SiteSettingsSpaceComponent);

const SiteSettingsSpaceAngularHOC = angularProviderHOC<SiteSettingsSpaceOwnProps, SiteSettingsAngularProps, SiteSettingsAngularProps>({
  getAngularServicesWithServiceLocator: () => ({
    server: getSessionManagementServiceInstance(),
  }),
  selectPropsFromAngularServices: angularServices => angularServices,
});

export const SiteSettingsSpace: React.ComponentType<SiteSettingsSpaceOwnProps>
  = SiteSettingsSpaceAngularHOC(ConnectedSiteSettingsSpace);
